/* ============================================================
   Mock data — portat din claude_design/data.js
   Simulează operațiuni Yang Ming.
   TODO: înlocuiește cu apeluri API când se conectează backend-ul.
   ============================================================ */

export interface Company {
  id: string;
  name: string;
  short: string;
  color: string;
  vat: string;
}

export interface Port {
  code: string;
  name: string;
  country: string;
  lng: number;
  lat: number;
}

export interface StatusDef {
  id: string;
  labelRo: string;
  labelEn: string;
  badge: string;
}

export interface Container {
  no: string;
  type: string;
  status: string;
  pol: string;
  pod: string;
  vessel: string;
  voyage: string;
  etd: string;
  eta: string;
  weight: number;
  commodity: string;
  bls: string[];
  booking: string;
  company: string;
  progress: number;
  demurrageDays: number;
  deliveredAt?: string;
}

export interface Booking {
  ref: string;
  company: string;
  pol: string;
  pod: string;
  containers: string;
  commodity: string;
  vessel: string;
  etd: string;
  eta: string;
  status: string;
  price: number;
}

export interface BL {
  no: string;
  type: string;
  status: string;
  pol: string;
  pod: string;
  shipper: string;
  consignee: string;
  vessel: string;
  voyage: string;
  issued: string;
  containers: number;
  freight: string;
  company: string;
  booking: string;
  splitOf?: string;
  note?: string;
}

export interface OfferRow {
  pol?: string;
  pod?: string;
  prices?: Record<string, number>;
  labelRo?: string;
  labelEn?: string;
  descRo?: string;
  descEn?: string;
  price?: number;
  currency?: string;
  vat?: boolean;
}

export interface OfferSection {
  kind: 'freight' | 'thc' | 'land';
  rows: OfferRow[];
}

export interface OfferLine {
  pol: string;
  pod: string;
  type: string;
  price: number;
}

export interface Offer {
  ref: string;
  company: string;
  issued: string;
  validFrom: string;
  validUntil: string;
  status: string;
  containerTypes: string[];
  sections: OfferSection[];
  pol?: string;
  pod?: string;
  type?: string;
  price?: number;
  currency?: string;
  lines?: OfferLine[];
}

export interface ShipDeparture {
  vessel: string;
  voyage: string;
  port: string;
  etd: string;
  service: string;
  to: string[];
}

export interface Route {
  pol: string;
  pod: string;
  transit: number;
  frequency: 'weekly' | 'biweekly';
  service: string;
}

export const COMPANIES: Company[] = [
  { id: 'co_aurora', name: 'Aurora Trading SRL', short: 'AUR', color: '#1e6bd6', vat: 'RO38291045' },
  { id: 'co_carpathia', name: 'Carpathia Logistics SA', short: 'CRP', color: '#0c8a5b', vat: 'RO29472810' },
  { id: 'co_blacksea', name: 'Black Sea Imports SRL', short: 'BSI', color: '#b6500f', vat: 'RO41902733' },
];

export const PORTS: Port[] = [
  { code: 'ROCND', name: 'Constanța', country: 'RO', lng: 28.66, lat: 44.18 },
  { code: 'DEHAM', name: 'Hamburg', country: 'DE', lng: 9.99, lat: 53.55 },
  { code: 'NLRTM', name: 'Rotterdam', country: 'NL', lng: 4.4, lat: 51.92 },
  { code: 'BEANR', name: 'Antwerp', country: 'BE', lng: 4.4, lat: 51.23 },
  { code: 'GRPIR', name: 'Piraeus', country: 'GR', lng: 23.65, lat: 37.94 },
  { code: 'ESALG', name: 'Algeciras', country: 'ES', lng: -5.45, lat: 36.13 },
  { code: 'AEJEA', name: 'Jebel Ali', country: 'AE', lng: 55.06, lat: 25.01 },
  { code: 'SGSIN', name: 'Singapore', country: 'SG', lng: 103.85, lat: 1.29 },
  { code: 'CNSHA', name: 'Shanghai', country: 'CN', lng: 121.47, lat: 31.23 },
  { code: 'CNNGB', name: 'Ningbo', country: 'CN', lng: 121.55, lat: 29.87 },
  { code: 'CNQIN', name: 'Qingdao', country: 'CN', lng: 120.31, lat: 36.07 },
  { code: 'KRPUS', name: 'Busan', country: 'KR', lng: 129.07, lat: 35.1 },
  { code: 'INMAA', name: 'Chennai', country: 'IN', lng: 80.27, lat: 13.08 },
  { code: 'INNSA', name: 'Nhava Sheva', country: 'IN', lng: 72.95, lat: 18.95 },
  { code: 'VNSGN', name: 'Ho Chi Minh', country: 'VN', lng: 106.7, lat: 10.78 },
  { code: 'IDJKT', name: 'Jakarta', country: 'ID', lng: 106.81, lat: -6.2 },
];

export const portByCode = (c: string): Port | undefined => PORTS.find((p) => p.code === c);

export const VESSELS = [
  'YM WARRANTY', 'YM WORTH', 'YM WONDROUS', 'YM WELLNESS',
  'YM WIDTH', 'YM WHOLESOME', 'YM WISDOM', 'YM WARMTH',
  'YM TRUTH', 'YM TRIUMPH', 'YM TIPTOP', 'YM TROPHY',
  'YM UPSURGENCE', 'YM UTOPIA', 'YM UTMOST', 'YM UBIQUITY',
];

export const CONTAINER_TYPES = [
  { code: '20DV', label: "20' Standard" },
  { code: '40DV', label: "40' Standard" },
  { code: '40HC', label: "40' High Cube" },
  { code: '20RF', label: "20' Reefer" },
  { code: '40RF', label: "40' Reefer" },
  { code: '40OT', label: "40' Open Top" },
];

export const CONTAINER_STATUSES: Record<string, StatusDef> = {
  // Export — statusuri suplimentare pentru containerele care pleacă din Constanța.
  // (Backendul le trimite pe același câmp `status`, alături de cele de import.)
  booked: { id: 'booked', labelRo: 'Rezervat', labelEn: 'Booked', badge: 'badge-neutral' },
  loaded: { id: 'loaded', labelRo: 'Încărcat', labelEn: 'Loaded', badge: 'badge-accent' },
  in_transit: { id: 'in_transit', labelRo: 'În tranzit', labelEn: 'In transit', badge: 'badge-info' },
  transshipment: { id: 'transshipment', labelRo: 'Transshipment', labelEn: 'Transshipment', badge: 'badge-hub' },
  black_sea: { id: 'black_sea', labelRo: 'Marea Neagră', labelEn: 'Black Sea', badge: 'badge-warn' },
  discharged: { id: 'discharged', labelRo: 'Descărcat', labelEn: 'Discharged', badge: 'badge-info' },
  in_delivery: { id: 'in_delivery', labelRo: 'În livrare', labelEn: 'In delivery', badge: 'badge-accent' },
  completed: { id: 'completed', labelRo: 'Finalizat', labelEn: 'Completed', badge: 'badge-success' },
};

export const BL_STATUSES: Record<string, StatusDef> = {
  draft: { id: 'draft', labelRo: 'Draft', labelEn: 'Draft', badge: 'badge-neutral' },
  released: { id: 'released', labelRo: 'Eliberat', labelEn: 'Released', badge: 'badge-success' },
  surrendered: { id: 'surrendered', labelRo: 'Predat', labelEn: 'Surrendered', badge: 'badge-success' },
  telex: { id: 'telex', labelRo: 'Telex Release', labelEn: 'Telex Release', badge: 'badge-info' },
  pending: { id: 'pending', labelRo: 'În așteptare', labelEn: 'Pending', badge: 'badge-warn' },
};

export const OFFER_STATUSES: Record<string, StatusDef> = {
  valid: { id: 'valid', labelRo: 'Valabilă', labelEn: 'Valid', badge: 'badge-success' },
  expired: { id: 'expired', labelRo: 'Expirată', labelEn: 'Expired', badge: 'badge-neutral' },
};

export const dDate = (offsetDays: number): string => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.toISOString().slice(0, 10);
};

export const fmtDate = (iso?: string | null, lang: 'ro' | 'en' = 'ro'): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (lang === 'ro') {
    return d.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const CONTAINERS: Container[] = [
  { no: 'YMLU3819472', type: '40HC', status: 'transshipment', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WARRANTY', voyage: '048W', etd: dDate(-12), eta: dDate(6), weight: 18450, commodity: 'Electronics / consumer goods', bls: ['YMJA178293041', 'YMJA178293041B'], booking: 'TLS25-0042', company: 'co_aurora', progress: 0.62, demurrageDays: 0 },
  { no: 'YMLU2901834', type: '40HC', status: 'in_transit', pol: 'CNNGB', pod: 'ROCND', vessel: 'YM WONDROUS', voyage: '051W', etd: dDate(-8), eta: dDate(10), weight: 17200, commodity: 'Textiles & garments', bls: ['YMJA178293055'], booking: 'TLS25-0043', company: 'co_aurora', progress: 0.45, demurrageDays: 0 },
  { no: 'YMLU7384021', type: '20DV', status: 'in_transit', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WORTH', voyage: '049W', etd: dDate(1), eta: dDate(20), weight: 22100, commodity: 'Auto parts', bls: ['YMJA178293098'], booking: 'TLS25-0051', company: 'co_carpathia', progress: 0.08, demurrageDays: 0 },
  { no: 'YMLU5562017', type: '40HC', status: 'in_delivery', pol: 'CNQIN', pod: 'ROCND', vessel: 'YM WELLNESS', voyage: '046W', etd: dDate(-32), eta: dDate(-3), weight: 19800, commodity: 'Furniture', bls: ['YMJA178292811'], booking: 'TLS25-0021', company: 'co_blacksea', progress: 1.0, demurrageDays: 2 },
  { no: 'YMLU8814509', type: '40DV', status: 'black_sea', pol: 'VNSGN', pod: 'ROCND', vessel: 'YM TRUTH', voyage: '022W', etd: dDate(-15), eta: dDate(4), weight: 16700, commodity: 'Coffee beans', bls: ['YMJA178293112'], booking: 'TLS25-0048', company: 'co_blacksea', progress: 0.72, demurrageDays: 0 },
  { no: 'YMLU4471230', type: '20DV', status: 'in_transit', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WIDTH', voyage: '050W', etd: dDate(3), eta: dDate(22), weight: 21500, commodity: 'Machinery parts', bls: ['YMJA178293120'], booking: 'TLS25-0054', company: 'co_aurora', progress: 0.02, demurrageDays: 0 },
  { no: 'YMLU6720381', type: '40HC', status: 'completed', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WHOLESOME', voyage: '044W', etd: dDate(-44), eta: dDate(-16), weight: 18200, commodity: 'Consumer electronics', bls: ['YMJA178292650'], booking: 'TLS25-0014', company: 'co_carpathia', progress: 1.0, demurrageDays: 0, deliveredAt: dDate(-13) },
  { no: 'YMLU9920104', type: '40RF', status: 'transshipment', pol: 'VNSGN', pod: 'ROCND', vessel: 'YM TRIUMPH', voyage: '021W', etd: dDate(-10), eta: dDate(9), weight: 20100, commodity: 'Frozen seafood', bls: ['YMJA178293071'], booking: 'TLS25-0046', company: 'co_blacksea', progress: 0.55, demurrageDays: 0 },
  { no: 'YMLU1183920', type: '40HC', status: 'black_sea', pol: 'CNNGB', pod: 'ROCND', vessel: 'YM WISDOM', voyage: '047W', etd: dDate(-25), eta: dDate(2), weight: 17800, commodity: 'Home appliances', bls: ['YMJA178293002'], booking: 'TLS25-0033', company: 'co_aurora', progress: 0.78, demurrageDays: 0 },
  { no: 'YMLU3357210', type: '20DV', status: 'completed', pol: 'INNSA', pod: 'ROCND', vessel: 'YM TIPTOP', voyage: '019W', etd: dDate(-50), eta: dDate(-22), weight: 23400, commodity: 'Spices & dry goods', bls: ['YMJA178292540'], booking: 'TLS25-0009', company: 'co_blacksea', progress: 1.0, demurrageDays: 0, deliveredAt: dDate(-19) },
  { no: 'YMLU5588104', type: '40DV', status: 'in_transit', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WARMTH', voyage: '052W', etd: dDate(7), eta: dDate(26), weight: 19200, commodity: 'Industrial chemicals', bls: [], booking: 'TLS25-0058', company: 'co_aurora', progress: 0, demurrageDays: 0 },
  { no: 'YMLU7790231', type: '40HC', status: 'black_sea', pol: 'CNSHA', pod: 'DEHAM', vessel: 'YM WARRANTY', voyage: '048W', etd: dDate(-12), eta: dDate(3), weight: 18600, commodity: 'Solar panels', bls: ['YMJA178293044'], booking: 'TLS25-0042', company: 'co_aurora', progress: 0.78, demurrageDays: 0 },
  { no: 'YMLU2104783', type: '40HC', status: 'discharged', pol: 'IDJKT', pod: 'ROCND', vessel: 'YM UPSURGENCE', voyage: '017W', etd: dDate(-28), eta: dDate(-1), weight: 17100, commodity: 'Rubber products', bls: ['YMJA178292890', 'YMJA178292890B'], booking: 'TLS25-0028', company: 'co_carpathia', progress: 1.0, demurrageDays: 0 },
  { no: 'YMLU8843129', type: '20RF', status: 'transshipment', pol: 'CNQIN', pod: 'ROCND', vessel: 'YM UTOPIA', voyage: '018W', etd: dDate(-7), eta: dDate(11), weight: 21700, commodity: 'Pharmaceuticals', bls: ['YMJA178293087'], booking: 'TLS25-0049', company: 'co_carpathia', progress: 0.38, demurrageDays: 0 },
  { no: 'YMLU4490012', type: '40HC', status: 'in_delivery', pol: 'KRPUS', pod: 'ROCND', vessel: 'YM UTMOST', voyage: '016W', etd: dDate(-31), eta: dDate(-4), weight: 18900, commodity: 'Auto components', bls: ['YMJA178292801'], booking: 'TLS25-0020', company: 'co_carpathia', progress: 1.0, demurrageDays: 1 },
  { no: 'YMLU6601128', type: '20DV', status: 'in_transit', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WIDTH', voyage: '050W', etd: dDate(3), eta: dDate(22), weight: 0, commodity: '—', bls: [], booking: 'TLS25-0054', company: 'co_aurora', progress: 0, demurrageDays: 0 },
  { no: 'YMLU3398721', type: '40DV', status: 'black_sea', pol: 'INMAA', pod: 'ROCND', vessel: 'YM TROPHY', voyage: '020W', etd: dDate(-18), eta: dDate(0), weight: 16400, commodity: 'Cotton textiles', bls: ['YMJA178293022'], booking: 'TLS25-0037', company: 'co_blacksea', progress: 0.92, demurrageDays: 0 },
  { no: 'YMLU7012984', type: '40HC', status: 'completed', pol: 'CNSHA', pod: 'ROCND', vessel: 'YM WONDROUS', voyage: '045W', etd: dDate(-42), eta: dDate(-14), weight: 18800, commodity: 'Electronics', bls: ['YMJA178292610'], booking: 'TLS25-0011', company: 'co_aurora', progress: 1.0, demurrageDays: 0, deliveredAt: dDate(-11) },
];

export const BOOKINGS: Booking[] = [
  { ref: 'TLS25-0058', company: 'co_aurora', pol: 'CNSHA', pod: 'ROCND', containers: '2 × 40HC', commodity: 'Industrial chemicals', vessel: 'YM WARMTH', etd: dDate(7), eta: dDate(26), status: 'in_transit', price: 2890 },
  { ref: 'TLS25-0054', company: 'co_aurora', pol: 'CNSHA', pod: 'ROCND', containers: '1 × 20DV', commodity: 'Machinery parts', vessel: 'YM WIDTH', etd: dDate(3), eta: dDate(22), status: 'in_transit', price: 1640 },
  { ref: 'TLS25-0051', company: 'co_carpathia', pol: 'CNSHA', pod: 'ROCND', containers: '1 × 20DV', commodity: 'Auto parts', vessel: 'YM WORTH', etd: dDate(1), eta: dDate(20), status: 'in_transit', price: 1720 },
  { ref: 'TLS25-0049', company: 'co_carpathia', pol: 'CNQIN', pod: 'ROCND', containers: '1 × 20RF', commodity: 'Pharmaceuticals', vessel: 'YM UTOPIA', etd: dDate(-7), eta: dDate(11), status: 'transshipment', price: 3210 },
  { ref: 'TLS25-0048', company: 'co_blacksea', pol: 'VNSGN', pod: 'ROCND', containers: '1 × 40DV', commodity: 'Coffee beans', vessel: 'YM TRUTH', etd: dDate(-15), eta: dDate(4), status: 'black_sea', price: 2480 },
  { ref: 'TLS25-0046', company: 'co_blacksea', pol: 'VNSGN', pod: 'ROCND', containers: '1 × 40RF', commodity: 'Frozen seafood', vessel: 'YM TRIUMPH', etd: dDate(-10), eta: dDate(9), status: 'transshipment', price: 3680 },
  { ref: 'TLS25-0043', company: 'co_aurora', pol: 'CNNGB', pod: 'ROCND', containers: '1 × 40HC', commodity: 'Textiles', vessel: 'YM WONDROUS', etd: dDate(-8), eta: dDate(10), status: 'in_transit', price: 2780 },
  { ref: 'TLS25-0042', company: 'co_aurora', pol: 'CNSHA', pod: 'ROCND', containers: '2 × 40HC', commodity: 'Electronics', vessel: 'YM WARRANTY', etd: dDate(-12), eta: dDate(6), status: 'transshipment', price: 5740 },
];

const _OFFERS_RAW: Omit<Offer, 'pol' | 'pod' | 'type' | 'price' | 'currency' | 'lines'>[] = [
  {
    ref: 'OFF-2025-0142', company: 'co_aurora',
    issued: dDate(-1), validFrom: dDate(-1), validUntil: dDate(8),
    status: 'valid', containerTypes: ['20DV', '40HC'],
    sections: [
      { kind: 'freight', rows: [
        { pol: 'CNSHA', pod: 'ROCND', prices: { '20DV': 1640, '40HC': 2850 } },
        { pol: 'CNNGB', pod: 'ROCND', prices: { '20DV': 1610, '40HC': 2780 } },
        { pol: 'CNQIN', pod: 'ROCND', prices: { '20DV': 1690, '40HC': 2910 } },
      ]},
      { kind: 'thc', rows: [
        { labelRo: 'THC origine · port încărcare', labelEn: 'Origin THC · loading port', prices: { '20DV': 145, '40HC': 215 } },
        { labelRo: 'THC destinație · Constanța', labelEn: 'Destination THC · Constanța', prices: { '20DV': 180, '40HC': 265 } },
        { labelRo: 'Documentație B/L', labelEn: 'B/L documentation', prices: { '20DV': 45, '40HC': 45 } },
      ]},
      { kind: 'land', rows: [
        { descRo: 'Constanța → București · camion 24t', descEn: 'Constanța → Bucharest · 24t truck', price: 1280, currency: 'RON', vat: true },
        { descRo: 'Constanța → Cluj-Napoca · camion 24t', descEn: 'Constanța → Cluj-Napoca · 24t truck', price: 2480, currency: 'RON', vat: true },
        { descRo: 'Constanța → Timișoara · camion 24t', descEn: 'Constanța → Timișoara · 24t truck', price: 2980, currency: 'RON', vat: true },
      ]},
    ],
  },
  {
    ref: 'OFF-2025-0135', company: 'co_carpathia',
    issued: dDate(-7), validFrom: dDate(-7), validUntil: dDate(2),
    status: 'valid', containerTypes: ['20RF', '40RF'],
    sections: [
      { kind: 'freight', rows: [
        { pol: 'CNQIN', pod: 'ROCND', prices: { '20RF': 3210, '40RF': 4280 } },
        { pol: 'CNSHA', pod: 'ROCND', prices: { '20RF': 3150, '40RF': 4150 } },
      ]},
      { kind: 'thc', rows: [
        { labelRo: 'THC origine reefer', labelEn: 'Origin reefer THC', prices: { '20RF': 195, '40RF': 295 } },
        { labelRo: 'THC destinație · Constanța', labelEn: 'Destination THC · Constanța', prices: { '20RF': 230, '40RF': 340 } },
        { labelRo: 'Monitorizare temperatură', labelEn: 'Temperature monitoring', prices: { '20RF': 85, '40RF': 120 } },
      ]},
      { kind: 'land', rows: [
        { descRo: 'Constanța → București · camion reefer', descEn: 'Constanța → Bucharest · reefer truck', price: 1680, currency: 'RON', vat: true },
        { descRo: 'Constanța → Iași · camion reefer', descEn: 'Constanța → Iași · reefer truck', price: 2150, currency: 'RON', vat: true },
      ]},
    ],
  },
  {
    ref: 'OFF-2025-0134', company: 'co_carpathia',
    issued: dDate(-12), validFrom: dDate(-12), validUntil: dDate(-2),
    status: 'expired', containerTypes: ['20DV', '40DV', '40HC'],
    sections: [
      { kind: 'freight', rows: [
        { pol: 'CNSHA', pod: 'ROCND', prices: { '20DV': 1580, '40DV': 2470, '40HC': 2530 } },
        { pol: 'CNNGB', pod: 'ROCND', prices: { '20DV': 1560, '40DV': 2440, '40HC': 2510 } },
      ]},
      { kind: 'thc', rows: [
        { labelRo: 'THC origine', labelEn: 'Origin THC', prices: { '20DV': 135, '40DV': 205, '40HC': 215 } },
        { labelRo: 'THC destinație Constanța', labelEn: 'Destination THC Constanța', prices: { '20DV': 170, '40DV': 250, '40HC': 260 } },
      ]},
      { kind: 'land', rows: [
        { descRo: 'Constanța → București · camion 24t', descEn: 'Constanța → Bucharest · 24t truck', price: 1180, currency: 'RON', vat: true },
      ]},
    ],
  },
  {
    ref: 'OFF-2025-0133', company: 'co_blacksea',
    issued: dDate(-2), validFrom: dDate(-2), validUntil: dDate(12),
    status: 'valid', containerTypes: ['20DV', '40DV', '40RF'],
    sections: [
      { kind: 'freight', rows: [
        { pol: 'VNSGN', pod: 'ROCND', prices: { '20DV': 1640, '40DV': 2480, '40RF': 3680 } },
        { pol: 'INNSA', pod: 'ROCND', prices: { '20DV': 1890, '40DV': 2740 } },
        { pol: 'INMAA', pod: 'ROCND', prices: { '20DV': 1740, '40DV': 2610 } },
      ]},
      { kind: 'thc', rows: [
        { labelRo: 'THC origine', labelEn: 'Origin THC', prices: { '20DV': 150, '40DV': 220, '40RF': 290 } },
        { labelRo: 'THC destinație · Constanța', labelEn: 'Destination THC · Constanța', prices: { '20DV': 180, '40DV': 270, '40RF': 340 } },
      ]},
      { kind: 'land', rows: [
        { descRo: 'Constanța → București', descEn: 'Constanța → Bucharest', price: 1280, currency: 'RON', vat: true },
      ]},
    ],
  },
  {
    ref: 'OFF-2025-0125', company: 'co_carpathia',
    issued: dDate(-5), validFrom: dDate(-5), validUntil: dDate(7),
    status: 'valid', containerTypes: ['20DV', '40HC'],
    sections: [
      { kind: 'freight', rows: [
        { pol: 'KRPUS', pod: 'ROCND', prices: { '20DV': 1820, '40HC': 3120 } },
      ]},
      { kind: 'thc', rows: [
        { labelRo: 'THC origine Busan', labelEn: 'Origin THC · Busan', prices: { '20DV': 175, '40HC': 265 } },
        { labelRo: 'THC destinație · Constanța', labelEn: 'Destination THC · Constanța', prices: { '20DV': 180, '40HC': 285 } },
      ]},
      { kind: 'land', rows: [
        { descRo: 'Constanța → București · camion 24t', descEn: 'Constanța → Bucharest · 24t truck', price: 1280, currency: 'RON', vat: true },
      ]},
    ],
  },
  {
    ref: 'OFF-2025-0115', company: 'co_aurora',
    issued: dDate(-6), validFrom: dDate(-6), validUntil: dDate(4),
    status: 'valid', containerTypes: ['40HC'],
    sections: [
      { kind: 'freight', rows: [
        { pol: 'CNSHA', pod: 'DEHAM', prices: { '40HC': 2240 } },
        { pol: 'CNSHA', pod: 'BEANR', prices: { '40HC': 2310 } },
        { pol: 'CNSHA', pod: 'NLRTM', prices: { '40HC': 2290 } },
      ]},
      { kind: 'thc', rows: [
        { labelRo: 'THC origine · Shanghai', labelEn: 'Origin THC · Shanghai', prices: { '40HC': 215 } },
      ]},
      { kind: 'land', rows: [
        { descRo: 'Hamburg → București · camion 24t', descEn: 'Hamburg → Bucharest · 24t truck', price: 1980, currency: 'EUR', vat: true },
      ]},
    ],
  },
];

export const OFFERS: Offer[] = _OFFERS_RAW.map((o) => {
  const freight = o.sections.find((s) => s.kind === 'freight');
  const firstRow = freight?.rows[0];
  const firstType = o.containerTypes[0];
  const pol = firstRow?.pol;
  const pod = firstRow?.pod;
  const type = firstType;
  const price = firstRow && firstType ? firstRow.prices?.[firstType] : undefined;
  const lines: OfferLine[] = (freight?.rows || []).flatMap((r) =>
    o.containerTypes
      .filter((tt) => r.prices?.[tt] != null)
      .map((tt) => ({ pol: r.pol!, pod: r.pod!, type: tt, price: r.prices![tt] })),
  );
  return { ...o, pol, pod, type, price, currency: 'USD', lines };
});

export const SHIP_DEPARTURES: ShipDeparture[] = [
  { vessel: 'YM WIDTH', voyage: '050W', port: 'CNSHA', etd: dDate(3), service: 'ASN', to: ['CNNGB', 'SGSIN', 'ROCND'] },
  { vessel: 'YM WARMTH', voyage: '052W', port: 'CNSHA', etd: dDate(7), service: 'ASN', to: ['CNNGB', 'ROCND'] },
  { vessel: 'YM UTMOST', voyage: '017W', port: 'KRPUS', etd: dDate(2), service: 'KME', to: ['CNQIN', 'SGSIN', 'ROCND'] },
  { vessel: 'YM WONDROUS', voyage: '053W', port: 'CNNGB', etd: dDate(5), service: 'ASN', to: ['CNSHA', 'ROCND'] },
  { vessel: 'YM TROPHY', voyage: '021W', port: 'INMAA', etd: dDate(4), service: 'IME', to: ['INNSA', 'GRPIR', 'ROCND'] },
  { vessel: 'YM UTOPIA', voyage: '019W', port: 'CNQIN', etd: dDate(8), service: 'KME', to: ['KRPUS', 'ROCND'] },
];

export const ROUTES: Route[] = [
  { pol: 'CNSHA', pod: 'ROCND', transit: 19, frequency: 'weekly', service: 'ASN' },
  { pol: 'CNNGB', pod: 'ROCND', transit: 21, frequency: 'weekly', service: 'ASN' },
  { pol: 'CNQIN', pod: 'ROCND', transit: 22, frequency: 'weekly', service: 'KME' },
  { pol: 'KRPUS', pod: 'ROCND', transit: 28, frequency: 'weekly', service: 'KME' },
  { pol: 'VNSGN', pod: 'ROCND', transit: 24, frequency: 'biweekly', service: 'ASN' },
  { pol: 'INNSA', pod: 'ROCND', transit: 26, frequency: 'weekly', service: 'IME' },
  { pol: 'INMAA', pod: 'ROCND', transit: 24, frequency: 'weekly', service: 'IME' },
  { pol: 'IDJKT', pod: 'ROCND', transit: 27, frequency: 'biweekly', service: 'ASN' },
  { pol: 'CNSHA', pod: 'DEHAM', transit: 32, frequency: 'weekly', service: 'FE3' },
];

export interface ComputedStats {
  active: number;
  inTransit: number;
  arrivingSoon: number;
}

export const computeStats = (
  containers: Container[],
  companyId: string,
): ComputedStats => {
  const inCompany = (c: Container) => companyId === 'all' || c.company === companyId;
  const active = containers.filter((c) => inCompany(c) && c.status !== 'completed');
  const inTransit = containers.filter(
    (c) => inCompany(c) && ['in_transit', 'transshipment', 'black_sea'].includes(c.status),
  );
  const arrivingSoon = containers.filter((c) => {
    if (!inCompany(c)) return false;
    const days = Math.floor((new Date(c.eta).getTime() - Date.now()) / 86400000);
    return days >= 0 && days <= 14 && c.status !== 'completed';
  });
  return { active: active.length, inTransit: inTransit.length, arrivingSoon: arrivingSoon.length };
};

export const daysFromNow = (iso: string): number =>
  Math.round((new Date(iso).getTime() - Date.now()) / 86400000);

export const etaLabel = (iso: string, lang: 'ro' | 'en'): string => {
  const d = daysFromNow(iso);
  if (d === 0) return lang === 'ro' ? 'Astăzi' : 'Today';
  if (d === 1) return lang === 'ro' ? 'Mâine' : 'Tomorrow';
  if (d > 0) return lang === 'ro' ? `în ${d} zile` : `in ${d} days`;
  return lang === 'ro' ? `acum ${-d} zile` : `${-d} days ago`;
};

export const statusLabel = (
  statusMap: Record<string, StatusDef>,
  statusId: string,
  lang: 'ro' | 'en',
): string => {
  const s = statusMap[statusId];
  if (!s) return statusId;
  return lang === 'ro' ? s.labelRo : s.labelEn;
};
